import {api} from '../../contexts/AuthContext';
import {
    AIProviderInfo,
    AnalysisRequest,
    AnalysisResult,
    EnvelopedPaginatedResponse
} from '../../types';

interface StreamingCallbacks {
    onProgress?: (data: {
        current: number;
        total: number;
        currentChunk: number;
        totalChunks: number;
        status: string
    }) => void;
    onChunkStart?: (data: { chunkIndex: number; totalChunks: number; urlsInChunk: number }) => void;
    onChunkComplete?: (data: {
        chunkIndex: number;
        results: AnalysisResult[];
        safeCount: number;
        totalCount: number;
        chunkCost: number
    }) => void;
    onChunkError?: (data: { chunkIndex: number; error: string; results: AnalysisResult[] }) => void;
    onWarning?: (data: { message: string; urlsFound?: number; urlsRequested?: number }) => void;
    onComplete?: (data: { summary: any; totalCost: number; analyzedAt: string; bulkResultId?: string }) => void;
    onError?: (error: string) => void;
}

// A dropped socket (lid close, transient network blip, or the backend poll
// loop hitting HAProxy's 60s client-idle timeout) must not surface as a
// failure while the underlying analysis is still running — analysis_queue
// keeps the durable status/results, and GET /api/analysis/reattach/:queueId
// re-opens an SSE view onto it (bd deepcontext-xoiv.3). Bounded so a
// permanently unreachable job still gives up instead of looping forever.
const MAX_REATTACH_ATTEMPTS = 5;

interface StreamRecoveryState {
    queueId?: string;
    streamStarted: boolean;
}

function buildStreamUrl(endpoint: string): string {
    // Use the same base URL configuration as the api instance. Handle slash
    // concatenation explicitly to avoid double slashes.
    const baseURL = api.defaults.baseURL || '';
    return baseURL ? `${baseURL.replace(/\/$/, '')}${endpoint}` : endpoint;
}

function fetchReattachStream(queueId: string, signal: AbortSignal): Promise<Response> {
    return fetch(buildStreamUrl(`/api/analysis/reattach/${encodeURIComponent(queueId)}`), {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            'Accept': 'text/event-stream',
        },
        credentials: 'include',
        signal,
    });
}

/**
 * Reads one SSE response body to a terminal event ('complete', or 'error'
 * that isn't marked recoverable) or a drop. A drop is either the connection
 * closing before any terminal event arrived, or a recoverable server error
 * (the poll-ceiling timeout, which still has a live queueId to resume) —
 * both are handled identically by the caller via reattachment.
 */
async function consumeEventStream(
    body: ReadableStream<Uint8Array>,
    callbacks: StreamingCallbacks,
    state: StreamRecoveryState
): Promise<'terminal' | 'dropped'> {
    const reader = body.getReader();
    const decoder = new TextDecoder();

    try {
        while (true) {
            const {done, value} = await reader.read();

            if (done) return 'dropped';

            const chunk = decoder.decode(value, {stream: true});
            const lines = chunk.split('\n');

            let currentEvent = '';

            for (const line of lines) {
                if (line.startsWith('event: ')) {
                    currentEvent = line.substring(7).trim();
                    continue;
                }

                if (line.startsWith('data: ')) {
                    const dataString = line.substring(6).trim();
                    if (dataString) {
                        try {
                            const data = JSON.parse(dataString);
                            if (data?.queueId) state.queueId = data.queueId;

                            switch (currentEvent) {
                                case 'progress':
                                    callbacks.onProgress?.(data);
                                    break;
                                case 'chunk_start':
                                    callbacks.onChunkStart?.(data);
                                    break;
                                case 'chunk_complete':
                                    callbacks.onChunkComplete?.(data);
                                    break;
                                case 'chunk_error':
                                    callbacks.onChunkError?.(data);
                                    break;
                                case 'warning':
                                    callbacks.onWarning?.(data);
                                    break;
                                case 'complete':
                                    callbacks.onComplete?.(data);
                                    return 'terminal';
                                case 'error':
                                    if (data.recoverable && state.queueId) {
                                        return 'dropped';
                                    }
                                    callbacks.onError?.(data.error || 'Unknown error');
                                    return 'terminal';
                                default:
                                    console.log('Unknown event type:', currentEvent, data);
                            }

                            currentEvent = '';
                        } catch (parseError) {
                            console.warn('Failed to parse SSE data:', dataString, parseError);
                        }
                    }
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}

/**
 * Drives a streaming analysis SSE connection, transparently reattaching by
 * queueId (bd deepcontext-xoiv.3) instead of surfacing a drop as a failure
 * whenever a queueId has already been observed.
 */
function streamAnalysis(
    fetchInitial: (signal: AbortSignal) => Promise<Response>,
    callbacks: StreamingCallbacks,
    startErrorMessage: string
): { abort: () => void } {
    const controller = new AbortController();
    const state: StreamRecoveryState = {streamStarted: false};

    const attempt = async (fetchFn: (signal: AbortSignal) => Promise<Response>, reattachCount: number): Promise<void> => {
        try {
            const response = await fetchFn(controller.signal);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            if (!response.body) {
                throw new Error('Response body is null');
            }

            state.streamStarted = true;
            const outcome = await consumeEventStream(response.body, callbacks, state);
            if (outcome === 'dropped') {
                await recover(reattachCount);
            }
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                console.log('Stream aborted by user');
                return;
            }
            await recover(reattachCount, error);
        }
    };

    const recover = async (reattachCount: number, error?: unknown): Promise<void> => {
        if (state.queueId && reattachCount < MAX_REATTACH_ATTEMPTS && !controller.signal.aborted) {
            console.log(`Socket dropped mid-analysis — reattaching to queue ${state.queueId} (attempt ${reattachCount + 1}/${MAX_REATTACH_ATTEMPTS})`);
            await attempt((signal) => fetchReattachStream(state.queueId!, signal), reattachCount + 1);
            return;
        }
        if (error) {
            console.error(state.streamStarted ? 'Stream processing error:' : 'Failed to start streaming analysis:', error);
        }
        callbacks.onError?.(state.streamStarted ? 'Stream processing failed' : startErrorMessage);
    };

    attempt(fetchInitial, 0);

    return {
        abort: () => controller.abort()
    };
}

// Analysis API
export const analysisApi = {
    analyzeStreaming: async (
        request: AnalysisRequest,
        callbacks: StreamingCallbacks
    ): Promise<{ abort: () => void }> => {
        return streamAnalysis(
            (signal) => fetch(buildStreamUrl('/api/analysis/analyze-streaming'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                    'Accept': 'text/event-stream',
                },
                credentials: 'include',
                body: JSON.stringify(request),
                signal,
            }),
            callbacks,
            'Failed to start analysis'
        );
    },

    // Resumes streaming an in-flight or already-finished analysis by queue id
    // after a dropped socket, instead of the caller having to start over
    // (bd deepcontext-xoiv.3). analyzeStreaming already reattaches
    // automatically on a drop once it has seen a queueId; this is the entry
    // point for a caller that already knows the queueId (e.g. persisted
    // across a tab reload).
    reattach: async (
        queueId: string,
        callbacks: StreamingCallbacks
    ): Promise<{ abort: () => void }> => {
        return streamAnalysis(
            (signal) => fetchReattachStream(queueId, signal),
            callbacks,
            'Failed to reattach to analysis'
        );
    },

    getHistory: async (
        page: number = 1,
        limit: number = 10,
        filters?: {
            promptId?: string;
            isBrandSafe?: boolean;
            provider?: string;
            model?: string;
            market?: string;
        },
        signal?: AbortSignal,
        companyId?: string
    ): Promise<EnvelopedPaginatedResponse<AnalysisResult>> => {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });

        // Add filter parameters if provided
        if (filters) {
            if (filters.promptId && filters.promptId !== 'all') {
                params.append('promptId', filters.promptId);
            }
            if (filters.isBrandSafe !== undefined) {
                params.append('isBrandSafe', filters.isBrandSafe.toString());
            }
            if (filters.provider && filters.provider !== 'all') {
                params.append('provider', filters.provider);
            }
            if (filters.model && filters.model !== 'all') {
                params.append('model', filters.model);
            }
            // MarketPill filter (bd deepcontext-z5mx.2) — narrows history to a
            // single market key resolved by GET /api/analysis/markets.
            if (filters.market && filters.market !== 'all') {
                params.append('market', filters.market);
            }
        }

        // eskimi_admin/adops staff have no company of their own — the
        // selected company travels as an explicit query param, same as
        // promptApi.getAll (bd deepcontext-jvwh.3).
        if (companyId) {
            params.append('companyId', companyId);
        }

        // Increase timeout for history queries which can take longer with large datasets
        const response = await api.get(`/api/analysis/history?${params}`, {
            timeout: 120000, // 2 minutes timeout
            signal
        });
        return response.data;
    },

    getById: async (id: string): Promise<AnalysisResult> => {
        const response = await api.get(`/api/analysis/${id}`);
        return response.data.result;
    },

    getSummary: async (promptId?: string, signal?: AbortSignal, companyId?: string, market?: string): Promise<{
        success: boolean;
        summary: {
            totalUrls: number;
            matchedUrls: number;
            unmatchedUrls: number;
            matchRate: number;
            averageConfidence: number;
        };
    }> => {
        const params = new URLSearchParams();
        if (promptId) params.set('promptId', promptId);
        // eskimi_admin/adops staff have no company of their own — the
        // selected company travels as an explicit query param, same as
        // promptApi.getAll (bd deepcontext-jvwh.3).
        if (companyId) params.set('companyId', companyId);
        // MarketPill filter (bd deepcontext-z5mx.2) — narrows KPI tiles to a
        // single market key resolved by GET /api/analysis/markets.
        if (market && market !== 'all') params.set('market', market);
        const query = params.toString();
        const response = await api.get(`/api/analysis/summary${query ? `?${query}` : ''}`, {
            signal
        });
        return response.data;
    },

    // Per-market result counts for a blueprint's history (bd deepcontext-z5mx.1
    // backend, deepcontext-z5mx.2 frontend) — drives the Market pill next to
    // the Blueprint pill on Analysis Audit.
    getMarkets: async (promptId: string, signal?: AbortSignal, companyId?: string): Promise<{
        success: boolean;
        markets: Array<{ market: string; label: string; resultCount: number }>;
    }> => {
        const params = new URLSearchParams({ promptId });
        if (companyId) params.set('companyId', companyId);
        const response = await api.get(`/api/analysis/markets?${params}`, { signal });
        return response.data;
    },

    getDailyAvails: async (promptId: string, signal?: AbortSignal): Promise<{
        success: boolean;
        /** null means the value was never computed — distinct from a genuine zero. */
        dailyAvails: number | null;
    }> => {
        const response = await api.get(`/api/analysis/daily-avails?promptId=${promptId}`, {
            signal
        });
        return response.data;
    },

    getBatchDailyAvails: async (items: Array<{ url_hash: string; is_brand_safe: boolean }>): Promise<{
        success: boolean;
        results: Record<string, number>;
    }> => {
        const response = await api.post('/api/analysis/batch-daily-avails', {items});
        return response.data;
    },

    getBidderSyncStatus: async (promptId: string, signal?: AbortSignal): Promise<{
        success: boolean;
        syncedCount: number;
        notSyncedCount: number;
        failedCount: number;
        totalMatchedUrls: number;
        syncPercentage: number;
    }> => {
        const response = await api.get(`/api/analysis/bidder-sync-status?promptId=${promptId}`, {
            signal
        });
        return response.data;
    },

    getProviders: async (): Promise<AIProviderInfo[]> => {
        const response = await api.get('/api/analysis/providers');
        return response.data.providers;
    },

    getDatabaseUrls: async (limit: number = 10, countryCode?: string, domains?: string[], excludeHomepages?: boolean, options?: {
        promptId?: string;
        countryCodes?: string[];
        countryDistributionMode?: 'uniform' | 'proportional';
    }): Promise<{
        urls: string[];
        records: any[];
        totalReturned: number
    }> => {
        const params = new URLSearchParams({limit: limit.toString()});
        if (countryCode && countryCode.trim()) {
            params.append('countryCode', countryCode.trim());
        }
        if (domains && domains.length > 0) {
            params.append('domains', domains.join(','));
        }
        if (excludeHomepages !== undefined) {
            params.append('excludeHomepages', String(excludeHomepages));
        }
        // Blueprint-aware preview (bd deepcontext-zaon): mirrors what Analyze scans.
        if (options?.promptId) {
            params.append('promptId', options.promptId);
            if (options.countryCodes && options.countryCodes.length > 0) {
                params.append('countryCodes', options.countryCodes.join(','));
            }
            if (options.countryDistributionMode) {
                params.append('countryDistributionMode', options.countryDistributionMode);
            }
        }
        const response = await api.get(`/api/analysis/database-urls?${params}`);
        return {
            urls: response.data.urls,
            records: response.data.records,
            totalReturned: response.data.totalReturned
        };
    },

    getUrlInformation: async (url: string): Promise<{
        success: boolean;
        url: string;
        totalResults: number;
        results: Array<{
            promptId: string;
            isBrandSafe: boolean;
            confidence: number;
            reasoning: string;
            contentSummary: string;
            analyzedAt: Date;
            provider: string;
            model: string;
        }>;
    }> => {
        const encodedUrl = encodeURIComponent(url);
        const response = await api.get(`/api/analysis/url/${encodedUrl}`);
        return response.data;
    },

    getUrlMatches: async (url: string): Promise<{
        brand_safety: string[];
        audiences: string[];
        scan_status: {
            has_been_scanned: boolean;
            scanned_brand_safety_prompts: string[];
            scanned_audience_prompts: string[];
            total_prompts_scanned: number;
        };
    }> => {
        const encodedUrl = encodeURIComponent(url);
        const response = await api.get(`/api/analysis/url/${encodedUrl}/matches`);
        return response.data;
    },

    getBulkSummaries: async (promptId?: string): Promise<{
        success: boolean;
        summary: {
            totalJobs: number;
            totalUrls: number;
            totalMatchedUrls: number;
            totalUnmatchedUrls: number;
            totalCost: number;
            totalInputTokens: number;
            totalOutputTokens: number;
            averageConfidence: number;
        };
        jobs: Array<{
            id: string;
            promptId: string;
            analyzedAt: string;
            summary: {
                totalUrls: number;
                safeUrls: number;
                unsafeUrls: number;
                averageConfidence: number;
                totalInputTokens?: number;
                totalOutputTokens?: number;
            };
            totalCost: number;
        }>;
        /**
         * The unfiltered listing (no promptId) is capped server-side —
         * `bulk_scan_jobs` has ~193k rows and cannot be sorted or shipped
         * whole. `jobsTruncated` says whether `jobs` is the complete set;
         * `jobsLimit` is the cap that was applied. When it is true the MySQL
         * fallback `summary` covers only the jobs returned.
         */
        jobsLimit: number;
        jobsTruncated: boolean;
        dataSource?: 'mysql' | 'clickhouse';
    }> => {
        const params = promptId ? `?promptId=${promptId}` : '';
        const response = await api.get(`/api/analysis/bulk-summaries${params}`);
        return response.data;
    },

    getBulkJobDetails: async (bulkJobId: string): Promise<{
        success: boolean;
        bulkJob: {
            id: string;
            promptId: string;
            promptName: string;
            promptDescription: string;
            promptContent: string;
            promptType: 'brand-safety' | 'audience-targeting';
            analyzedAt: string;
            totalUrls: number;
            matchedUrls: number;
            unmatchedUrls: number;
            averageConfidence: number;
            totalCost: number;
        };
        matchedSamples: AnalysisResult[];
        unmatchedSamples: AnalysisResult[];
    }> => {
        const response = await api.get(`/api/analysis/bulk-jobs/${bulkJobId}/export`);
        return response.data;
    },
};
